'use client';

import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyFinancialsResponse,
  CompanyHistory,
  CompanyLegalStatus,
  CompanyNetworkResponse,
  CompanyRegisterResponse,
  GesellschafterItem,
  InsolvencyRecordItem,
} from '@/lib/backend-api';
import type { CompanyCountryCode } from '@/lib/countries';
import type { TopEmployerRankLink } from '@/lib/seo-lists/types';
import type { useSession } from '@/hooks/use-session';
import type { BalanceSheetFlatRow } from '@/new_components/balance_sheet/balanceSheetService';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import type {
  CompanyProfileHeroData,
  CompanyProfileStatistics,
} from './CompanyProfileSection';
import type { buildValidationErrors } from './companyDetailDerived';
import { CompanyProfileSection } from './CompanyProfileSection';
import { MOBILE_ORDER } from './companyDetailLayout';
import { PeopleTabbedCard } from './PeopleTabbedCard';
import { CompanyHistoryCard } from './CompanyHistoryCard';
import { CompanyValidationErrorsCard } from './CompanyValidationErrorsCard';
import { CompanyAdminActionsCard } from './CompanyAdminActionsCard';
import { AdminFinancialTablesCard } from './AdminFinancialTablesCard';
import type { TFunction } from 'i18next';

interface CompanyDetailMainColumnProps {
  basicData: CompanyBasicResponse | null;
  effectiveRegisterData: CompanyRegisterResponse | null;
  effectiveFinancialsData: CompanyFinancialsResponse | null;
  contactData: CompanyContactResponse | null;
  heroData: CompanyProfileHeroData;
  statistics: CompanyProfileStatistics;
  loadingBackground: boolean;
  euId: string;
  ssrNetworkData: CompanyNetworkResponse | null | undefined;
  topEmployerRank: TopEmployerRankLink | null | undefined;
  networkReloadToken: number;
  insolvencyRecords: InsolvencyRecordItem[];
  legalStatus: CompanyLegalStatus;
  hasGesellschafterliste: boolean;
  country: CompanyCountryCode;
  gesellschafterItems: GesellschafterItem[];
  companyHistory: CompanyHistory | null;
  session: ReturnType<typeof useSession>['data'];
  showRegisterAttribution: boolean;
  attributionRegisterId: string | null;
  isAdminUser: boolean;
  aktivaData: BalanceSheetFlatRow[] | null;
  passivaData: BalanceSheetFlatRow[] | null;
  guvData: GuvFlatRow[] | null;
  aiGuvData: GuvFlatRow[] | null;
  t: TFunction;
  validationErrors: ReturnType<typeof buildValidationErrors>;
  availableFinancialYears: number[];
}

export function CompanyDetailMainColumn({
  basicData,
  effectiveRegisterData,
  effectiveFinancialsData,
  contactData,
  heroData,
  statistics,
  loadingBackground,
  euId,
  ssrNetworkData,
  topEmployerRank,
  networkReloadToken,
  insolvencyRecords,
  legalStatus,
  hasGesellschafterliste,
  country,
  gesellschafterItems,
  companyHistory,
  session,
  showRegisterAttribution,
  attributionRegisterId,
  isAdminUser,
  aktivaData,
  passivaData,
  guvData,
  aiGuvData,
  t,
  validationErrors,
  availableFinancialYears,
}: CompanyDetailMainColumnProps) {
  return (
    <div className="contents lg:flex lg:flex-col lg:order-1 min-w-0 lg:overflow-hidden">
      {basicData && (
        <CompanyProfileSection
          className={MOBILE_ORDER.profile}
          mobileSplit={{ top: MOBILE_ORDER.profileTop, rest: MOBILE_ORDER.profileRest }}
          basicData={basicData}
          registerData={effectiveRegisterData}
          financialsData={effectiveFinancialsData}
          contactData={contactData}
          heroData={heroData}
          statistics={statistics}
          isLoadingBackground={loadingBackground}
          euId={euId}
          networkData={ssrNetworkData}
          topEmployerRank={topEmployerRank}
          networkReloadToken={networkReloadToken}
          insolvencyRecords={insolvencyRecords}
          legalStatus={legalStatus}
          isDeleted={heroData.isDeleted}
        />
      )}

      {!heroData.isDeleted &&
        (effectiveRegisterData?.participants?.length ||
          hasGesellschafterliste ||
          (effectiveFinancialsData?.subsidiaries?.length ?? 0) > 0) && (
          <PeopleTabbedCard
            country={country}
            className={MOBILE_ORDER.people}
            participants={effectiveRegisterData?.participants ?? []}
            gesellschafter={gesellschafterItems}
            hasGesellschafterliste={hasGesellschafterliste}
            kommanditisten={companyHistory?.kommanditisten ?? []}
            // Only show Kommanditanteil % for GmbH/UG/AG & Co. KG. In a
            // standalone KG the Komplementär also holds ownership we can't
            // see from the CD, so the Kommanditisten render as owners
            // without a (misleading) percentage.
            showKommanditPercent={/[&+]\s*Co\./i.test(
              basicData?.display_name ?? '',
            )}
            subsidiaries={effectiveFinancialsData?.subsidiaries ?? []}
            isAuthenticated={!!session}
            euId={euId}
            showRegisterAttribution={showRegisterAttribution}
            registerCourt={basicData?.register_court}
            registerId={attributionRegisterId}
            capturedAt={basicData?.updated_at}
          />
        )}


      {/* Tenders live here as a tab rather than in a card of their
          own. The mount guard has to admit tenders separately:
          `companyHistory` is null without a CD event aggregate, and
          38% of the companies that have tenders have no CD row — under
          a `companyHistory &&` guard alone their Vergabe tab would
          never render. Tender data rides along on the SSR basic
          payload, so there is no second fetch and no layout shift. */}
      {(companyHistory || (basicData?.tenders?.length ?? 0) > 0) && (
        <CompanyHistoryCard
          country={country}
          className={MOBILE_ORDER.history}
          history={companyHistory ?? null}
          isAuthenticated={!!session}
          insolvencyRecords={insolvencyRecords}
          tenders={basicData?.tenders ?? []}
          cpvProducts={basicData?.cpv_products ?? []}
          tendersTotal={basicData?.tenders_total ?? 0}
          euId={euId}
          showRegisterAttribution={showRegisterAttribution}
          registerCourt={basicData?.register_court}
          registerId={attributionRegisterId}
          capturedAt={basicData?.updated_at}
        />
      )}

      {/* Admin-only detailed financial tables */}
      {isAdminUser &&
        !!(
          aktivaData?.length ||
          passivaData?.length ||
          guvData?.length ||
          aiGuvData?.length
        ) && (
          <AdminFinancialTablesCard
            t={t}
            aktivaData={aktivaData}
            passivaData={passivaData}
            guvData={guvData}
            aiGuvData={aiGuvData}
            euId={euId}
          />
        )}

      {/* Admin-only validation errors */}
      {isAdminUser && validationErrors.total > 0 && (
        <CompanyValidationErrorsCard
          className={MOBILE_ORDER.adminValidation}
          validationErrors={validationErrors}
        />
      )}

      {/* Admin actions */}
      {isAdminUser && (
        <CompanyAdminActionsCard
          className={MOBILE_ORDER.adminActions}
          euId={euId}
          availableYears={availableFinancialYears}
        />
      )}
    </div>
  );
}
